/**
 * Cat3Toggle — the Scope Builder "Use Cat-3 line items" switch and its rule
 * (PRP-MCP-7 decision 0049, PRP-MCP-9 G8). Flood is Category 3 by peril and an NFIP
 * claim is Category 3, so the switch is ON and locked with the reason shown. A storm
 * (hurricane, wind, storm) is NOT assumed Category 3: it defaults OFF and the user turns
 * it on when the water is grossly contaminated. A category stored on the estimate wins
 * over the default: 3 turns it on, 1 or 2 keep it off.
 */
import { cn } from '../../lib/utils';
import { Switch } from './ui/switch';

export const FLOOD_CAT3_REASON = 'Flood water is Category 3.';
export const NFIP_CAT3_REASON = 'Flood claims under NFIP are Category 3.';

/** The water category stored on a project (1, 2 or 3), or null. */
export function readStoredCategory(project: unknown): number | null {
  const raw = (project as { water_category?: unknown } | null)?.water_category;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return n === 1 || n === 2 || n === 3 ? n : null;
}

/** The toggle's starting value for a stored category (only 3 turns it on). */
export function storedCat3Default(storedCategory?: number | null): boolean {
  return storedCategory === 3;
}

/** Locked-on reason for the Cat-3 toggle, or null when the user may choose. */
export function cat3LockReason(dt: string, floodClaim: boolean): string | null {
  if (dt === 'flood') return FLOOD_CAT3_REASON;
  if (floodClaim) return NFIP_CAT3_REASON;
  return null;
}

interface Cat3ToggleProps {
  damageType: string;
  floodClaim: boolean;
  /** The user's own choice (ignored while locked). */
  cat3: boolean;
  set: (_v: boolean) => void;
}

/** Switch + label + hint; locked on with the reason as the hint for flood / NFIP. */
export function Cat3Toggle({ damageType, floodClaim, cat3, set }: Cat3ToggleProps) {
  const lock = cat3LockReason(damageType, floodClaim);
  return (
    <label className={cn('flex gap-[10px] items-start', lock ? 'cursor-not-allowed' : 'cursor-pointer')}>
      <Switch checked={lock ? true : cat3} onCheckedChange={set} disabled={lock !== null} className="mt-px" />
      <span>
        <span className="text-[12.5px] font-[530] text-[var(--tx-hi)]">
          Use Cat-3 line items (contaminated water)
        </span>
        <span className="block text-[11px] text-[var(--tx-faint)]">
          {lock ??
            'Off unless the water is grossly contaminated (Category 3), such as sewage, storm surge or rising river water. Uses WTR Category 3 codes.'}
        </span>
      </span>
    </label>
  );
}
